import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import { Switch } from '../Switch'
import { PlanHuntDriveRow } from './PlanHuntDriveRow'
import { PlanHuntDriveEditor } from './PlanHuntDriveEditor'
import { ConfirmDeleteModal } from '../ConfirmDeleteModal'
import { isStandPin } from '../../lib/mapPinTypes'
import type { PinTypeCatalog } from '../../lib/pinTypeCatalog'
import type { GeoArea, MapObject } from '../../lib/types'
import type { MapAppearance } from '../../lib/mapAppearance'
import type { PlanHuntDrive, PlanHuntMember } from './types'

export interface PlanHuntDrivesStepProps {
  drives: PlanHuntDrive[]
  onChange: (drives: PlanHuntDrive[]) => void
  teamPins: MapObject[]
  /** Every parcel of the ground, plus its sub-areas and no-hunting zones. */
  boundaryAreas?: readonly GeoArea[]
  forbiddenPolygons?: readonly GeoArea[]
  appearance: MapAppearance
  teamBorderColor?: string
  /** Team-map sub-areas offered as drive bases. */
  subAreas?: GeoArea[]
  participants: PlanHuntMember[]
  /** Pin-type catalog for labelling the per-drive map-pin groups by type. */
  pinTypeCatalog?: PinTypeCatalog
  /** Whether the per-drive sub-area + map-pin multi-selects are shown (the
   *  estate drive-subareas feature gate). */
  driveSubAreaScopeEnabled?: boolean
  teamName?: string
  /**
   * Whether to render the "What hunters see on the map" toggle at the top of the
   * step. This is the estate gate: an ordinary club hunt does not offer the
   * setting, so the whole row is absent and planning is unchanged. Presentation
   * only — the caller (the wizard) owns the estate decision and the value.
   */
  mapScopeVisible?: boolean
  /**
   * When on, the hunt map shows only the areas and stands belonging to the
   * planned drives; when off, hunters see the whole hunting ground (the default).
   * The .NET API derives the enforced filter from the drives themselves — this
   * flag is the only thing the wizard writes for map scope.
   */
  restrictMapToPlannedDrives?: boolean
  /** Persist the new value of the map-restriction toggle. */
  onToggleRestrictMapToPlannedDrives?: (value: boolean) => void
}

// The section headings are stored already upper-cased (matching the mobile
// strings verbatim), so no textTransform here — only the label look.
const infoHeadingStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1,
  color: COLORS.outline,
  marginBottom: 2,
}

function InfoSection({ heading, body }: { heading: string; body: string }) {
  return (
    <div>
      <div style={infoHeadingStyle}>{heading}</div>
      <div style={{ fontSize: 12, color: COLORS.onSurfaceVariant, lineHeight: 1.4 }}>{body}</div>
    </div>
  )
}

/**
 * The "What hunters see on the map" toggle shown at the top of the Drives step
 * for an estate hunt. One switch that restricts the hunt map to the planned
 * drives, plus an info popover explaining what it does, who it applies to, what
 * stays visible, and what turning it off means.
 *
 * Presentation only: the state and the persist come in as props. The actual map
 * filter is derived server-side from the drives — this control only sets the
 * on/off flag, so there is no per-area/per-pin hand-picking here.
 */
function MapRestrictionToggle({
  on,
  onToggle,
}: {
  on: boolean
  onToggle: (value: boolean) => void
}) {
  const { t } = useTranslation('planHunt')
  const [infoOpen, setInfoOpen] = useState(false)
  const infoRef = useRef<HTMLDivElement>(null)

  // Close the popover on Escape or an outside click — a hand-rolled popover gets
  // neither for free, and a keyboard user would otherwise be unable to dismiss it.
  useEffect(() => {
    if (!infoOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setInfoOpen(false) }
    const onClick = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) setInfoOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [infoOpen])

  return (
    <div
      className="wh-card"
      style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface }}>
            {t('mapRestriction.title')}
          </span>
          <div style={{ position: 'relative' }} ref={infoRef}>
            <button
              className="wh-btn-icon"
              aria-label={t('mapRestriction.infoLabel')}
              aria-expanded={infoOpen}
              onClick={() => setInfoOpen((v) => !v)}
            >
              <Icon name="info" size={16} />
            </button>
            {infoOpen && (
              <div
                role="dialog"
                aria-label={t('mapRestriction.title')}
                style={{
                  position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: 6, width: 280,
                  background: COLORS.surfaceContainerLowest, border: `1px solid ${COLORS.surfaceContainerHighest}`,
                  borderRadius: 10, padding: 14, boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}
              >
                <InfoSection heading={t('mapRestriction.whatDoesHeading')} body={t('mapRestriction.whatDoesBody')} />
                <InfoSection heading={t('mapRestriction.whoAppliesHeading')} body={t('mapRestriction.whoAppliesBody')} />
                <InfoSection heading={t('mapRestriction.staysVisibleHeading')} body={t('mapRestriction.staysVisibleBody')} />
                <InfoSection heading={t('mapRestriction.turningOffHeading')} body={t('mapRestriction.turningOffBody')} />
              </div>
            )}
          </div>
        </div>
        <div style={{ fontSize: 12, color: COLORS.outline }}>
          {on ? t('mapRestriction.subtitleOn') : t('mapRestriction.subtitleOff')}
        </div>
      </div>
      <Switch on={on} onChange={onToggle} aria-label={t('mapRestriction.title')} />
    </div>
  )
}

/**
 * Names of drives that have no stands picked. The backend rejects a draft
 * containing an empty drive (422), so the wizard blocks Continue on these and
 * tells the user which drive to fix. Drives themselves are optional — an empty
 * list is fine; only a drive that was added but left standless is invalid.
 */
export function emptyDriveNames(drives: PlanHuntDrive[]): string[] {
  return drives.filter((d) => d.stands.length === 0).map((d) => d.name)
}

export function PlanHuntDrivesStep({
  drives,
  onChange,
  teamPins,
  boundaryAreas = [],
  subAreas = [],
  forbiddenPolygons = [],
  appearance,
  teamBorderColor,
  participants,
  pinTypeCatalog,
  driveSubAreaScopeEnabled = false,
  teamName,
  mapScopeVisible = false,
  restrictMapToPlannedDrives = false,
  onToggleRestrictMapToPlannedDrives,
}: PlanHuntDrivesStepProps) {
  const { t } = useTranslation('planHuntDrives')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const updateDrive = (id: string, patch: Partial<PlanHuntDrive>) =>
    onChange(drives.map((d) => (d.id === id ? { ...d, ...patch } : d)))
  const removeDrive = (id: string) => {
    if (editingId === id) setEditingId(null)
    onChange(drives.filter((d) => d.id !== id))
  }
  const move = (id: string, dir: number) => {
    const i = drives.findIndex((d) => d.id === id)
    if (i < 0) return
    const j = i + dir
    if (j < 0 || j >= drives.length) return
    const next = [...drives]
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }
  const addDrive = () => {
    const id = `dr-${Date.now()}`
    // `area: null` stays the stored default — the entire ground is implied in
    // the UI (editor pre-selects it, the row labels it) but never written into
    // the drive, so persisted drafts don't pick up the ENTIRE_GROUND sentinel.
    onChange([...drives, { id, name: t('step.newDriveName', { number: drives.length + 1 }), area: null, stands: [] }])
    setEditingId(id)
  }

  const empties = emptyDriveNames(drives)
  const hasUnnamed = drives.some((d) => d.name.trim().length === 0)
  // Mirror mobile (`hasStandsOrTowers`): a drive only makes sense — and can
  // only be sent, since every drive needs 1–200 stands server-side — when the
  // team map has at least one stand/tower pin to place. With none, disable
  // "Add drive" and say why, instead of letting the user build a drive they can
  // never populate (which then 422s at send).
  const hasStands = teamPins.some(isStandPin)
  const editingDrive = drives.find((d) => d.id === editingId)
  const editingIndex = drives.findIndex((d) => d.id === editingId)
  const pendingDrive = drives.find((d) => d.id === pendingDeleteId)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {mapScopeVisible && (
        <MapRestrictionToggle
          on={restrictMapToPlannedDrives}
          onToggle={(v) => onToggleRestrictMapToPlannedDrives?.(v)}
        />
      )}
      {/* The generic intro is the step sub-heading in the wizard header; only
          the team-specific instruction stays in the step body (CLUB-579). */}
      {teamName && (
        <div style={{ fontSize: 14, color: COLORS.outline }}>
          <Trans
            t={t}
            i18nKey="step.introTeamSuffix"
            values={{ teamName }}
            components={{ 1: <b style={{ color: COLORS.onSurface }} /> }}
          />
        </div>
      )}
      {drives.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: 'center',
            border: `1.5px dashed ${COLORS.surfaceContainerHighest}`,
            borderRadius: 10,
            color: COLORS.outline,
            fontSize: 13,
            background: COLORS.surface,
          }}
        >
          {t('step.empty')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {drives.map((d, i) => (
            <PlanHuntDriveRow
              key={d.id}
              drive={d}
              index={i}
              total={drives.length}
              teamPins={teamPins}
              boundaryAreas={boundaryAreas}
              subAreas={subAreas}
              forbiddenPolygons={forbiddenPolygons}
              appearance={appearance}
              teamBorderColor={teamBorderColor}
              participants={participants}
              onEdit={() => setEditingId(d.id)}
              onRemove={() => setPendingDeleteId(d.id)}
              onMove={(dir) => move(d.id, dir)}
            />
          ))}
        </div>
      )}

      {hasUnnamed && (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: '10px 12px',
            borderRadius: 8,
            background: COLORS.errorContainer ?? '#fde8e8',
            color: COLORS.onErrorContainer ?? COLORS.error,
            fontSize: 13,
          }}
        >
          {t('step.unnamedWarning')}
        </p>
      )}

      {empties.length > 0 && (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: '10px 12px',
            borderRadius: 8,
            background: COLORS.errorContainer ?? '#fde8e8',
            color: COLORS.onErrorContainer ?? COLORS.error,
            fontSize: 13,
          }}
        >
          {empties.length === 1
            ? t('step.emptyStandsWarning', { count: 1, name: empties[0] })
            : t('step.emptyStandsWarning', { count: empties.length, names: empties.join(', ') })}
        </p>
      )}

      <button
        className="wh-btn wh-btn-outline"
        style={{ alignSelf: 'flex-start', opacity: hasStands ? 1 : 0.55 }}
        onClick={addDrive}
        disabled={!hasStands}
        title={hasStands ? undefined : t('step.addDriveNoStands')}
      >
        <Icon name="plus" size={16} />
        {t('step.addDrive')}
      </button>
      {!hasStands && (
        <p style={{ margin: 0, fontSize: 13, color: COLORS.outline }}>
          {t('step.addDriveNoStands')}
        </p>
      )}

      {editingDrive && (
        <PlanHuntDriveEditor
          drive={editingDrive}
          index={editingIndex}
          teamPins={teamPins}
          boundaryAreas={boundaryAreas}
          subAreas={subAreas}
          forbiddenPolygons={forbiddenPolygons}
          appearance={appearance}
          teamBorderColor={teamBorderColor}
          participants={participants}
          pinTypeCatalog={pinTypeCatalog}
          pinSelectionEnabled={driveSubAreaScopeEnabled}
          onChange={(patch) => updateDrive(editingDrive.id, patch)}
          onClose={() => setEditingId(null)}
        />
      )}

      {pendingDrive && (
        <ConfirmDeleteModal
          objectType="drive"
          objectName={pendingDrive.name.trim() || undefined}
          description={
            pendingDrive.stands.length > 0
              ? t('step.deleteDescriptionWithStands', { count: pendingDrive.stands.length })
              : t('step.deleteDescriptionEmpty')
          }
          onConfirm={() => { removeDrive(pendingDrive.id); setPendingDeleteId(null) }}
          onCancel={() => setPendingDeleteId(null)}
        />
      )}
    </div>
  )
}
